import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Database, TrendingUp, Brain, Shield, Zap, BarChart3 } from 'lucide-react';

const BigDataAnalyticsPage = () => {
  const services = [
    {
      icon: <Database className="w-8 h-8" />,
      title: "Data Infrastructure",
      description: "Build robust data infrastructure to collect, store, and process massive volumes of data. We design scalable solutions using modern data warehouses and lakes that grow with your business.",
      benefits: ["Cloud data warehouses", "ETL pipelines", "Real-time data processing"]
    },
    {
      icon: <BarChart3 className="w-8 h-8" />,
      title: "Advanced Analytics",
      description: "Transform raw data into actionable insights with advanced analytics. We employ statistical modeling, machine learning, and predictive analytics to uncover patterns and trends.",
      benefits: ["Predictive modeling", "Statistical analysis", "Trend forecasting"]
    },
    {
      icon: <Brain className="w-8 h-8" />,
      title: "Machine Learning Solutions",
      description: "Implement cutting-edge machine learning models to automate decision-making and optimize business processes. From recommendation engines to fraud detection systems.",
      benefits: ["Custom ML models", "AutoML solutions", "Model deployment"]
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: "Business Intelligence",
      description: "Create comprehensive BI dashboards and reports that provide real-time visibility into your business performance. Make data-driven decisions with interactive visualizations.",
      benefits: ["Interactive dashboards", "Custom reports", "KPI tracking"]
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Data Governance",
      description: "Ensure data quality, security, and compliance with robust governance frameworks. We implement best practices for data management, privacy, and regulatory compliance.",
      benefits: ["Data quality management", "Compliance frameworks", "Access controls"]
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: "Real-Time Analytics",
      description: "Process and analyze data in real-time to enable instant insights and automated responses. Perfect for fraud detection, monitoring systems, and dynamic pricing.",
      benefits: ["Stream processing", "Event-driven analytics", "Instant alerts"]
    }
  ];

  const industries = [
    { name: "Financial Services", description: "Risk analysis, fraud detection, algorithmic trading" },
    { name: "Retail & E-commerce", description: "Customer segmentation, demand forecasting, personalization" },
    { name: "Healthcare", description: "Patient analytics, treatment optimization, predictive diagnostics" },
    { name: "Manufacturing", description: "Predictive maintenance, quality control, supply chain optimization" }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-[#0d6571] to-[#0d6571]/80 py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="p-4 bg-white/10 rounded-xl">
              <Database className="w-12 h-12 text-white" />
            </div>
            <div>
              <h1 className="text-5xl font-bold text-white mb-2">
                Big Data Analytics
              </h1>
              <p className="text-xl text-white/90">
                Transform data into competitive advantage
              </p>
            </div>
          </div>
          <p className="text-lg text-white/80 max-w-3xl">
            Harness the power of big data to drive innovation, optimize operations, and make 
            smarter business decisions. Our comprehensive analytics solutions turn complex data 
            into clear, actionable insights.
          </p>
        </div>
      </div>

      {/* Services Grid */}
      <div className="py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Our Big Data Services
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              End-to-end big data solutions from infrastructure to insights
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all p-8 border-t-4 border-[#0d6571]"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#0d6571]/10 text-[#0d6571] mb-6">
                  {service.icon}
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  {service.title}
                </h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  {service.description}
                </p>
                <div className="space-y-2">
                  {service.benefits.map((benefit, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-gray-600">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#0d6571]"></div>
                      <span className="text-sm">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Industries Section */}
      <div className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Industry Solutions
            </h2>
            <p className="text-lg text-gray-600">
              Specialized big data analytics for your industry
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {industries.map((industry, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-gray-50 to-white p-8 rounded-xl border border-gray-200 hover:border-[#0d6571] transition-colors"
              >
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  {industry.name}
                </h3>
                <p className="text-gray-600">
                  {industry.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Technologies */}
      <div className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Technologies We Use
            </h2>
            <p className="text-lg text-gray-600">
              Industry-leading tools and platforms
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {['Apache Spark', 'Hadoop', 'AWS', 'Google Cloud', 'Python', 'TensorFlow', 'Tableau', 'Power BI'].map((tech, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition-shadow text-center"
              >
                <p className="font-semibold text-gray-800">{tech}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-br from-[#0d6571] to-[#0d6571]/80 py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-white mb-4">
            Ready to Unlock Your Data's Potential?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Let's discuss how big data analytics can transform your business. 
            Schedule a consultation with our data experts today.
          </p>
          <a
            href="/contact"
            className="inline-block px-8 py-4 bg-white text-[#0d6571] rounded-full hover:bg-gray-100 font-bold text-lg transition-colors"
          >
            Get Started Today
          </a>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default BigDataAnalyticsPage;
